import { useState } from 'react'


import { DataTable } from '@/admin/components/data-table'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ChevronRight } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import {
  type PartnershipHealthFinding,
  usePartnershipHealth,
  usePartnershipHealthFindings,
} from '@/admin/hooks/use-partnership-health'

/**
 * "What needs your attention?" for the partner network.
 *
 * Reads the stored scan; never runs one. A tile drills into the affected
 * records via a second, narrowed request rather than loading every finding up
 * front - a large book produces thousands.
 */
export function NetworkAttentionPanel({
  onOpenPartner,
}: {
  onOpenPartner: (uuid: string) => void
}) {
  const { t } = useTranslation()
  const [openCheck, setOpenCheck] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const { data, isPending } = usePartnershipHealth()
  const {
    data: findings,
    isPending: findingsPending,
    isFetching: findingsFetching,
  } = usePartnershipHealthFindings(openCheck, page, pageSize)

  if (isPending) {
    return (
      <p className="clara-small py-6" data-testid="attention-loading">
        {t('network.attention.loading', 'Loading...')}
      </p>
    )
  }

  // "Never scanned" and "nothing wrong" must not look the same - an empty
  // state here would read as a clean bill of health for a network nobody
  // has looked at.
  if (!data?.scan) {
    return (
      <div
        className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-5 py-6"
        data-testid="attention-never-scanned"
      >
        <p className="text-[13px] text-[var(--ink)]">
          {t('network.attention.neverScanned', 'This network has not been checked yet.')}
        </p>
        <p className="clara-small mt-1">
          {t(
            'network.attention.neverScannedHint',
            'Results appear here after the next scheduled check.',
          )}
        </p>
      </div>
    )
  }

  const checks = data.checks ?? []

  if (checks.length === 0) {
    return (
      <div
        className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-5 py-6"
        data-testid="attention-all-clear"
      >
        <p className="text-[13px] text-[var(--ink)]">
          {t('network.attention.allClear', 'Nothing needs attention.')}
        </p>
      </div>
    )
  }

  return (
    <div data-testid="attention-panel">
      {/* When it was checked, and a way to check again. These counts come from
          a stored scan and nothing schedules a refresh, so without the date the
          panel implies a freshness it does not have. */}
      {/* When it was checked. The "Check again" button is deliberately commented
          out below: a scan is a full pass over the book, and OC will carry far
          more partners than Supagard's 7,559, so a synchronous scan from a page
          is the wrong shape until it runs as a background task. The endpoint
          and its guards are kept (see PartnershipHealthRescanView) so the
          control can be restored the moment that exists. The date at least
          stops the panel implying a freshness it does not have. */}
      <div className="mb-4">
        <p className="clara-small">
          {t('network.attention.scannedCount', {
            count: data.scan.partnerships_scanned,
            defaultValue: '{{count}} partners checked',
          })}
          {' · '}
          {t('network.attention.checkedAt', 'checked {{when}}', {
            when: new Date(data.scan.started_at).toLocaleString(),
          })}
        </p>
      </div>

      {/* Restore with the background task - see the note above.
      <Button
        variant="outline"
        size="xs"
        data-testid="attention-rescan"
        disabled={rescan.isPending}
        onClick={() => {
          rescan
            .mutateAsync()
            .then(() => toast.success(t('network.attention.rescanDone', 'Checked just now.')))
            .catch((err) =>
              toastApiError(err, t('network.attention.rescanFailed', 'Could not check just now.')),
            )
        }}
      >
        {rescan.isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
        {rescan.isPending
          ? t('network.attention.rescanRunning', 'Checking...')
          : t('network.attention.rescan', 'Check again')}
      </Button>
      */}

      <div className="rounded-lg border-[0.5px] border-[var(--rule)] overflow-hidden">
        {checks.map((check) => {
          const isOpen = openCheck === check.key
          return (
            <div
              key={check.key}
              className="border-b-[0.5px] border-b-[var(--rule-light)] last:border-b-0"
            >
              <button
                type="button"
                data-testid={`attention-check-${check.key}`}
                onClick={() => {
                  // Page 1 for the new check: page 4 of the last one is
                  // meaningless here and would show an empty table.
                  setPage(1)
                  setOpenCheck(isOpen ? null : check.key)
                }}
                className="flex w-full cursor-pointer items-center gap-3 bg-transparent px-4 py-3 text-left hover:bg-[var(--bg-subtle)]"
              >
                <AlertCircle className="h-3.5 w-3.5 flex-none text-[var(--ink-3)]" />
                <span className="flex-1 text-[13px] text-[var(--ink)]">
                  {check.label}
                </span>
                <Badge variant="secondary">{check.count}</Badge>
                <ChevronRight
                  className={`h-3.5 w-3.5 flex-none text-[var(--ink-3)] ${
                    isOpen ? 'rotate-90' : ''
                  }`}
                />
              </button>

              {isOpen && (
                <div className="bg-[var(--bg-subtle)] px-4 pb-3">
                  <DataTable<PartnershipHealthFinding>
                    tableId="partnership-health-findings"
                    columns={[
                      {
                        id: 'name',
                        header: t('network.attention.recordHeader', 'Record'),
                        cell: (finding) =>
                          finding.name ||
                          t('network.attention.wholeNetwork', 'This network'),
                      },
                    ]}
                    data={findings?.results ?? []}
                    /* Both flags: isLoading alone lets a cached-empty page flash
                       the empty state while the real page is still in flight. */
                    isLoading={findingsPending}
                    isFetching={findingsFetching}
                    page={page}
                    pageSize={pageSize}
                    totalCount={findings?.count ?? 0}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size)
                      setPage(1)
                    }}
                    emptyMessage={t('network.attention.noRecords', 'No records.')}
                    rowKey={(finding) => finding.uuid}
                    onRowClick={(finding) =>
                      finding.partnership_uuid &&
                      onOpenPartner(finding.partnership_uuid)
                    }
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
